import { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import TourStep from './TourStep';
import { TOUR_STEPS, LS_KEYS } from './tourSteps';

// Post-onboarding guided tour orchestrator.
//
// Responsibilities:
//   1. Reads the current step from localStorage (LS_KEYS.step). Falls back to 0.
//   2. Locates the target element for the current step via `querySelector`.
//      Re-measures on window resize, scroll, and DOM mutations.
//   3. Renders a fixed full-screen dim backdrop via React Portal, with a
//      "hole" around the target element (box-shadow trick — no SVG mask).
//   4. Renders the TourStep tooltip positioned relative to the target.
//   5. Handles the two advance modes:
//        'click'  — clicking the target (or any descendant) advances the tour.
//                   Clicks OUTSIDE the target are blocked by the backdrop.
//        'button' — tooltip shows a «Далее» button; target clicks are ignored.
//   6. On finish: clears LS_KEYS.pending + LS_KEYS.step, sets LS_KEYS.done=1,
//      calls onFinish() so the parent can hide the tour and show a toast.
//
// The tour is NOT skippable — no X, no «Пропустить», no backdrop-close.

const SPOTLIGHT_PADDING = 8;
const TARGET_POLL_INTERVAL = 200; // ms — retry finding target if not yet in DOM
const TARGET_MISSING_TIMEOUT = 12000; // ms — wait for API data to load before auto-advancing

export default function OnboardingTour({ open, onFinish }) {
    const [stepIndex, setStepIndex] = useState(() => {
        const stored = localStorage.getItem(LS_KEYS.step);
        const parsed = stored != null ? parseInt(stored, 10) : 0;
        return Number.isFinite(parsed) && parsed >= 0 && parsed < TOUR_STEPS.length ? parsed : 0;
    });
    const [targetRect, setTargetRect] = useState(null);
    const [targetFound, setTargetFound] = useState(false);
    const pollTimerRef = useRef(null);
    const missingTimerRef = useRef(null);

    // Re-sync stepIndex from localStorage whenever `open` flips to true
    // (handles tour restart via the "Подсказки" button — without this the
    // component keeps the stale stepIndex from its initial mount).
    useEffect(() => {
        if (!open) return;
        const stored = localStorage.getItem(LS_KEYS.step);
        const parsed = stored != null ? parseInt(stored, 10) : 0;
        const fresh = Number.isFinite(parsed) && parsed >= 0 && parsed < TOUR_STEPS.length ? parsed : 0;
        setStepIndex(fresh);
        setTargetFound(false);
        setTargetRect(null);
    }, [open]);

    const currentStep = TOUR_STEPS[stepIndex];

    // ── Target discovery ────────────────────────────────────────────────
    const findTarget = useCallback(() => {
        if (!currentStep) return null;
        for (const selector of currentStep.selectors) {
            const el = document.querySelector(selector);
            if (el) return el;
        }
        return null;
    }, [currentStep]);

    const measureTarget = useCallback(() => {
        const el = findTarget();
        if (!el) {
            setTargetFound(false);
            setTargetRect(null);
            return false;
        }
        const rect = el.getBoundingClientRect();
        setTargetRect({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            bottom: rect.bottom,
            right: rect.right,
        });
        setTargetFound(true);

        // Scroll the target into view if it's offscreen.
        const vh = window.innerHeight;
        if (rect.top < 80 || rect.bottom > vh - 80) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return true;
    }, [findTarget]);

    // Step transitions — defined before the polling effect so the missing-target
    // auto-advance timer can reference it.
    const persistStep = (next) => {
        localStorage.setItem(LS_KEYS.step, String(next));
    };

    const finish = useCallback(() => {
        localStorage.removeItem(LS_KEYS.pending);
        localStorage.removeItem(LS_KEYS.step);
        localStorage.setItem(LS_KEYS.done, '1');
        if (onFinish) onFinish();
    }, [onFinish]);

    const advance = useCallback(() => {
        setStepIndex((prev) => {
            const next = prev + 1;
            if (next >= TOUR_STEPS.length) {
                finish();
                return prev;
            }
            persistStep(next);
            return next;
        });
        setTargetFound(false);
        setTargetRect(null);
    }, [finish]);

    // Poll for the target element on mount / step change — it may not exist
    // yet if PlansPage is still transitioning between levels.
    // If the target never appears within TARGET_MISSING_TIMEOUT, auto-advance
    // past this step so the user is never frozen on a dim screen (e.g., an
    // enrolled user skips the roadmap-list level entirely).
    useEffect(() => {
        if (!open) return;
        let found = false;
        const tick = () => {
            const ok = measureTarget();
            if (ok) {
                found = true;
                if (missingTimerRef.current) {
                    clearTimeout(missingTimerRef.current);
                    missingTimerRef.current = null;
                }
                return;
            }
            pollTimerRef.current = setTimeout(tick, TARGET_POLL_INTERVAL);
        };
        tick();
        // Safety-net: if target is still missing after TARGET_MISSING_TIMEOUT,
        // advance to the next step. Prevents permanent freeze on dim backdrop
        // when the current view doesn't contain the spotlighted element.
        missingTimerRef.current = setTimeout(() => {
            if (!found) {
                advance();
            }
        }, TARGET_MISSING_TIMEOUT);
        return () => {
            if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
            if (missingTimerRef.current) clearTimeout(missingTimerRef.current);
        };
    }, [open, stepIndex, measureTarget, advance]);

    // Re-measure on resize / scroll.
    useEffect(() => {
        if (!open || !targetFound) return;
        const onChange = () => measureTarget();
        window.addEventListener('resize', onChange);
        window.addEventListener('scroll', onChange, true);
        return () => {
            window.removeEventListener('resize', onChange);
            window.removeEventListener('scroll', onChange, true);
        };
    }, [open, targetFound, measureTarget]);

    // Re-measure on DOM mutations (e.g., PlansPage re-renders when a module
    // card opens the next level).
    useEffect(() => {
        if (!open) return;
        const observer = new MutationObserver(() => {
            measureTarget();
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: false,
        });
        return () => observer.disconnect();
    }, [open, measureTarget]);

    // ── Click interception on the target ───────────────────────────────
    // For `advance: 'click'` steps, we let the user's native click on the
    // target propagate (so PlansPage navigation fires), then advance the tour.
    useEffect(() => {
        if (!open || !targetFound || !currentStep || currentStep.advance !== 'click') return;
        const el = findTarget();
        if (!el) return;

        const onClick = () => {
            // Bump the step BEFORE the page re-renders / navigates so that when
            // PlansPage re-mounts (return from module/test page), it reads the
            // new step from localStorage.
            const next = stepIndex + 1;
            if (next >= TOUR_STEPS.length) {
                finish();
            } else {
                persistStep(next);
                setStepIndex(next);
                setTargetFound(false);
                setTargetRect(null);
            }
        };
        el.addEventListener('click', onClick, { capture: true });
        return () => {
            el.removeEventListener('click', onClick, { capture: true });
        };
    }, [open, targetFound, currentStep, stepIndex, findTarget, finish]);

    // Persist initial step on first mount so return-navigation knows where to resume.
    useEffect(() => {
        if (!open) return;
        if (localStorage.getItem(LS_KEYS.step) == null) {
            localStorage.setItem(LS_KEYS.step, String(stepIndex));
        }
    }, [open, stepIndex]);

    if (!open || !currentStep) return null;

    // ── Render ──────────────────────────────────────────────────────────
    // Spotlight is achieved with a transparent box covered by a giant
    // box-shadow that dims everything else. Clicks on the backdrop-shadow
    // area are swallowed (pointerEvents: 'auto' on backdrop), but clicks on
    // the spotlight rect pass through to the real target underneath
    // (pointerEvents: 'none' on the hole).
    const hasRect = targetRect != null;
    const holeStyle = hasRect
        ? {
            position: 'fixed',
            top: targetRect.top - SPOTLIGHT_PADDING,
            left: targetRect.left - SPOTLIGHT_PADDING,
            width: targetRect.width + SPOTLIGHT_PADDING * 2,
            height: targetRect.height + SPOTLIGHT_PADDING * 2,
            borderRadius: 12,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.72)',
            pointerEvents: 'none',
            zIndex: 10000,
            transition: 'all 0.25s ease',
        }
        : null;

    const glowStyle = hasRect
        ? {
            position: 'fixed',
            top: targetRect.top - SPOTLIGHT_PADDING,
            left: targetRect.left - SPOTLIGHT_PADDING,
            width: targetRect.width + SPOTLIGHT_PADDING * 2,
            height: targetRect.height + SPOTLIGHT_PADDING * 2,
            borderRadius: 12,
            border: '2px solid #DA7756',
            boxShadow: '0 0 0 4px rgba(218, 119, 86, 0.3)',
            pointerEvents: 'none',
            zIndex: 10001,
            animation: 'apex-tour-pulse 1.8s ease-in-out infinite',
        }
        : null;

    const fullscreenBackdropStyle = {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.72)',
        zIndex: 9999,
        pointerEvents: 'auto',
    };

    return createPortal(
        <>
            {/* Pulse keyframes (injected once). */}
            <style>{`
                @keyframes apex-tour-pulse {
                    0%, 100% { box-shadow: 0 0 0 4px rgba(218, 119, 86, 0.3); }
                    50%      { box-shadow: 0 0 0 8px rgba(218, 119, 86, 0.5); }
                }
            `}</style>

            <AnimatePresence>
                {!hasRect && (
                    // No target yet — render a full dim backdrop so the screen
                    // doesn't suddenly go bright between steps.
                    <motion.div
                        key="fullscreen-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={fullscreenBackdropStyle}
                    />
                )}
            </AnimatePresence>

            {hasRect && (
                <>
                    {/* Spotlight hole + dimming (via box-shadow trick). */}
                    <div style={holeStyle} />
                    {/* Glow ring on top of the target. */}
                    <div style={glowStyle} />
                    {/* Tooltip. */}
                    <TourStep
                        step={currentStep}
                        stepIndex={stepIndex}
                        totalSteps={TOUR_STEPS.length}
                        targetRect={targetRect}
                        onAdvance={advance}
                    />
                </>
            )}
        </>,
        document.body
    );
}
